import React, { useState, useEffect } from "react";
import {
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ChartBarIcon,
  ArrowPathIcon,
  CurrencyDollarIcon,
  ChartPieIcon,
  ArrowUpIcon,
} from "@heroicons/react/24/outline";
import { useTradingData } from "../contexts/TradingDataContext";

interface MainPairData {
  symbol: string;
  baseAsset: string;
  quoteAsset: string;
  price: number;
  changePercent24h: number;
  volume24h: number;
  marketCap: number;
  high24h: number;
  low24h: number;
  isUpdating?: boolean;
}

const CryptoDashboard: React.FC = () => {
  const { state } = useTradingData();
  const [updateTimeouts, setUpdateTimeouts] = useState<{[key: string]: NodeJS.Timeout}>({});

  // BTC 데이터 상태 (가상 데이터로 초기화)
  const [mainPair, setMainPair] = useState<MainPairData>({
    symbol: "BTCUSDT",
    baseAsset: "BTC", 
    quoteAsset: "USDT",
    price: 105650.49, // 실제 같은 가격으로 초기화
    changePercent24h: 0.143,
    volume24h: 1311226299.58,
    marketCap: 2081314653000,
    high24h: 106794.67,
    low24h: 104872.5,
    isUpdating: false,
  });

  const [globalMarket] = useState({
    totalMarketCap: 3100000000000,
    totalVolume24h: 89000000000,
    dominance: 60.5,
  });

  // TradingDataContext에서 받은 데이터로 상태 업데이트
  useEffect(() => {
    if (state.btcMarketData.price > 0) {
      setMainPair((prev) => ({
        ...prev,
        price: state.btcMarketData.price,
        changePercent24h: state.btcMarketData.priceChangePercent24h,
        volume24h: state.btcMarketData.volume24h,
        high24h: state.btcMarketData.high24h,
        low24h: state.btcMarketData.low24h,
        marketCap: state.btcMarketData.marketCap,
        isUpdating: true,
      }));

      // 업데이트 표시 제거
      const btcTimeout = setTimeout(() => {
        setMainPair((prev) => ({ ...prev, isUpdating: false }));
      }, 300);

      setUpdateTimeouts(prev => ({ ...prev, btc: btcTimeout }));
    }
  }, [state.btcMarketData]);

  // 연결 상태 확인
  useEffect(() => {
    if (state.isConnected) {
      console.log("✅ Connected to trading data stream");
    } else {
      console.log("❌ Disconnected from trading data stream");
    }
  }, [state.isConnected]);

  // cleanup timeouts
  useEffect(() => {
    return () => {
      Object.values(updateTimeouts).forEach(timeout => {
        if (timeout) clearTimeout(timeout);
      });
    };
  }, [updateTimeouts]);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(price);
  };

  const formatVolume = (volume: number) => {
    if (volume >= 1000000000) {
      return `$${(volume / 1000000000).toLocaleString('en-US', { 
        minimumFractionDigits: 1, 
        maximumFractionDigits: 1 
      })}B`;
    }
    if (volume >= 1000000) {
      return `$${(volume / 1000000).toLocaleString('en-US', { 
        minimumFractionDigits: 1, 
        maximumFractionDigits: 1 
      })}M`;
    }
    if (volume >= 1000) {
      return `$${(volume / 1000).toLocaleString('en-US', { 
        minimumFractionDigits: 1, 
        maximumFractionDigits: 1 
      })}K`;
    }
    return `$${volume.toLocaleString('en-US', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    })}`;
  };

  // 퍼센트 포맷팅 함수 추가
  const formatPercent = (percent: number) => {
    return percent.toLocaleString('en-US', { 
      minimumFractionDigits: 1, 
      maximumFractionDigits: 1 
    });
  };

  // 거래 중인 페어인지 확인하는 함수
  const isTradingPair = (symbol: string) => {
    // 기본값으로 BTCUSDT 사용
    const defaultTradingPair = 'BTCUSDT';
    return defaultTradingPair === symbol;
  };

  return (
    <div className="space-y-4">
      {/* 헤더 */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <ChartBarIcon className="h-5 w-5 text-blue-500" />
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
            암호화폐 대시보드
          </h3>
        </div>
        {/* 실시간 업데이트 표시 */}
        {state.isConnected && (
          <div className="flex items-center space-x-2 text-xs text-green-600 dark:text-green-400">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <span>실시간 연결됨</span>
          </div>
        )}
      </div>

      {/* 글로벌 크립토 마켓 */}
      <div className="bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-slate-800 dark:to-slate-700 rounded-xl p-4 border-2 border-blue-200 dark:border-slate-600 shadow-md">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              글로벌 크립토 마켓
            </h4>
            <div className="flex items-center space-x-4 text-xs text-slate-600 dark:text-slate-400">
              <span>시가총액: {formatVolume(globalMarket.totalMarketCap)}</span>
              <span>24h 거래량: {formatVolume(globalMarket.totalVolume24h)}</span>
              <span>BTC 도미넌스: {formatPercent(globalMarket.dominance)}%</span>
            </div>
          </div>
          <ChartPieIcon className="h-8 w-8 text-blue-500" />
        </div>
      </div>

      {/* 메인 거래쌍 (BTC/USDT) */}
      <div className="bg-white dark:bg-slate-800 rounded-xl p-6 border-2 border-slate-200 dark:border-slate-700 shadow-md">
        <div className="flex justify-between items-start mb-4">
          <div>
            <div className="flex items-center space-x-2">
              <span className="text-lg font-bold text-orange-500 dark:text-orange-400">
                {mainPair.baseAsset}/{mainPair.quoteAsset}
              </span>
              {/* 봇이 거래 중인 페어에만 "거래중" 버튼 표시 */}
              {isTradingPair(mainPair.symbol) && (
                <div className="px-2 py-1 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 text-xs font-medium rounded-md">
                  거래중
                </div>
              )}
              {/* 작은 업데이트 인디케이터 추가 */}
              {mainPair.isUpdating && (
                <div className="flex items-center space-x-1">
                  <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-ping"></div>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {mainPair.changePercent24h >= 0 ? (
              <ArrowTrendingUpIcon className="h-5 w-5 text-emerald-500" />
            ) : (
              <ArrowTrendingDownIcon className="h-5 w-5 text-red-500" />
            )}
            <span className={`text-sm font-medium ${
              mainPair.changePercent24h >= 0 
                ? "text-emerald-600 dark:text-emerald-400" 
                : "text-red-600 dark:text-red-400"
            }`}>
              {mainPair.changePercent24h >= 0 ? "+" : ""}{formatPercent(Math.abs(mainPair.changePercent24h))}%
            </span>
          </div>
        </div>

        <div className="mb-6">
          <div className="text-3xl font-bold text-slate-900 dark:text-white mb-1">
            {formatPrice(mainPair.price)}
          </div>
          <div className="text-sm text-slate-600 dark:text-slate-400">
            실시간 바이낸스 시세
          </div>
        </div>

        {/* 상세 정보 그리드 */}
        <div className="grid grid-cols-2 gap-3">
          {/* 24시간 고가 */}
          <div className="bg-white dark:bg-slate-800 rounded-xl p-4 border-2 border-slate-200 dark:border-slate-700 shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center space-x-2 mb-2">
                  <ArrowUpIcon className="h-4 w-4 text-emerald-500" />
                  <span className="text-xs text-slate-600 dark:text-slate-400 font-medium">24h 고가</span>
                </div>
                <div className="text-lg font-bold text-slate-900 dark:text-white">
                  {formatPrice(mainPair.high24h)}
                </div>
              </div>
            </div>
          </div>

          {/* 24시간 저가 */}
          <div className="bg-white dark:bg-slate-800 rounded-xl p-4 border-2 border-slate-200 dark:border-slate-700 shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center space-x-2 mb-2">
                  <ArrowTrendingDownIcon className="h-4 w-4 text-red-500" />
                  <span className="text-xs text-slate-600 dark:text-slate-400 font-medium">24h 저가</span>
                </div>
                <div className="text-lg font-bold text-slate-900 dark:text-white">
                  {formatPrice(mainPair.low24h)}
                </div>
              </div>
            </div>
          </div>

          {/* 24시간 거래량 */}
          <div className="bg-white dark:bg-slate-800 rounded-xl p-4 border-2 border-slate-200 dark:border-slate-700 shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center space-x-2 mb-2">
                  <CurrencyDollarIcon className="h-4 w-4 text-blue-500" />
                  <span className="text-xs text-slate-600 dark:text-slate-400 font-medium">24h 거래량</span>
                </div>
                <div className="text-lg font-bold text-slate-900 dark:text-white">
                  {formatVolume(mainPair.volume24h)}
                </div>
              </div>
            </div>
          </div>

          {/* 시가총액 */}
          <div className="bg-white dark:bg-slate-800 rounded-xl p-4 border-2 border-slate-200 dark:border-slate-700 shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center space-x-2 mb-2">
                  <ChartPieIcon className="h-4 w-4 text-purple-500" />
                  <span className="text-xs text-slate-600 dark:text-slate-400 font-medium">시가총액</span>
                </div>
                <div className="text-lg font-bold text-slate-900 dark:text-white">
                  {formatVolume(mainPair.marketCap)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CryptoDashboard; 